import { useEffect, useRef, type ReactNode } from 'react';

/**
 * The outline as a sheet, for screens with no room for the rail.
 *
 * <p>Without this the rail is simply absent below 1024px — and it takes the
 * khutbah's shape with it, which is the one thing the outline exists to show.
 * The design system is explicit that the sidebar becomes a drawer and the
 * sections become a sheet; vanishing was never the intent.
 *
 * <p>Built on {@code <dialog>} rather than a div with a high z-index, because
 * the browser then supplies what a hand-rolled modal usually gets wrong: focus
 * is trapped inside it, Escape closes it, and the rest of the page is inert to
 * both pointer and screen reader. Those are accessibility requirements, not
 * conveniences.
 */
export function OutlineSheet({
  open,
  onClose,
  railShownAtLg = true,
  children,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * Whether the outline is also showing as a permanent rail at `lg` and up.
   *
   * When it is, this sheet is the small-screen substitute and hides itself on
   * a wide display — two outlines at once would be a duplicate. When it is
   * not, at full reading width where the rail stands down to give the khutbah
   * the display, this sheet is the ONLY way to the outline and must open at
   * every width. Defaulting to true keeps the original behaviour for any
   * caller that does not say.
   */
  railShownAtLg?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) {
      return;
    }
    // showModal() is imperative and the DOM is the source of truth for whether
    // a dialog is open, so this is a genuine external system to synchronise
    // with rather than state to mirror.
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-label="Khutbah outline"
      // `close` fires for Escape and for close() alike, so the parent's state
      // cannot drift from the dialog's actual state.
      onClose={onClose}
      onClick={(event) => {
        // The backdrop is part of the dialog element, so a click landing on
        // the element itself rather than its contents is a backdrop click.
        if (event.target === ref.current) {
          onClose();
        }
      }}
      className={[
        'm-0 ms-0 me-auto h-dvh max-h-dvh w-72 max-w-[85vw] border-0 bg-sidebar p-0',
        'backdrop:bg-overlay',
        // Hidden on a wide display ONLY while the rail is there to replace
        // it. Without this condition, full reading width removed the rail and
        // left the sheet suppressed by `lg:hidden`, so the outline became
        // unreachable on a desktop — the button opened a dialog nothing could
        // see. Measured: the sheet's nav rendered at 0px wide.
        railShownAtLg ? 'lg:hidden' : '',
      ].join(' ')}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-center justify-between border-b border-sidebar-border px-4 py-3">
          <h2 className="text-sm font-semibold">Sections</h2>
          <button
            type="button"
            onClick={onClose}
            // 44px, the touch target the design system specifies.
            className="flex size-11 items-center justify-center rounded-md text-muted-foreground hover:bg-sidebar-accent"
          >
            <svg
              viewBox="0 0 24 24"
              width={18}
              height={18}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.75}
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
            <span className="sr-only">Close the outline</span>
          </button>
        </div>
        {/*
          The rail inside is hidden below lg by its own class, which is correct
          for the docked copy and wrong here. `[&>nav]:flex` re-shows it
          without the rail needing to know it has two homes — which also makes
          this work at `lg` and up, where the sheet now opens whenever the
          rail has stood down for full reading width.
        */}
        <div className="flex-1 overflow-y-auto [&>nav]:flex [&>nav]:w-full [&>nav]:border-e-0">
          {children}
        </div>
      </div>
    </dialog>
  );
}
